/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../../auth/services/auth.service';
import { SearchService } from '../../services/search.service';
import { catchError, of, switchMap, takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { Company } from '../../../../shared/interfaces/company';
import { LeadsService } from '../../../leads/services/leads.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';

@Component({
  selector: 'app-business-attributes-form',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './business-attributes-form.component.html',
  styleUrl: './business-attributes-form.component.scss',
})
export class BusinessAttributesFormComponent
  extends DestroyBase
  implements OnInit
{
  @Input() businessDetails: any;
  @Input() loanDetails: any;
  @Input() technicalAssistanceDetails: any;
  @Input() operationalToolsDetails: any;
  @Input() realEstateDetails: any;
  @Input() solarEnergyDetails: any;
  @Input() findingInvestorsDetails: any;
  @Input() initialValue: any;
  @Output() formSubmit = new EventEmitter<any>();

  leadAttributesForm: FormGroup;
  currentUsername: string | undefined;
  hasGetSetLeadsPermission = false;

  private addBusinessRecordService = inject(SearchService);
  private authService = inject(AuthService);
  private leadService = inject(LeadsService);

  constructor(private fb: FormBuilder) {
    super();
    this.leadAttributesForm = this.fb.group({
      leadStatus: [''],
      statusNotes: [''],
      viabilityRating: [''],
      seekingPartnership: [''],
      ownerGender: [''],
      ownerEthnicity: [''],
      ownerRace: [''],
      ownerDisability: [''],
    });
  }

  ngOnInit(): void {
    this.currentUsername = this.authService.getLoggedInUserInfo()?.name;
    this.hasGetSetLeadsPermission = this.authService.hasPermission(
      PermissionCode.GET_SET_LEADS,
    );
    if (this.initialValue) {
      this.leadAttributesForm.patchValue(this.initialValue);
    }
  }

  onSubmitForm() {
    if (this.leadAttributesForm.valid) {
      const formValues = this.leadAttributesForm.value;

      // Map business details to standardized property names
      const businessPropertyMap = {
        businessName: 'company_name',
        address: 'business_address',
        city: 'city',
        stateOrProvince: 'state',
        county: 'county',
        zipCode: 'zip_code',
        country: 'country',
        businessOwnerName: 'name',
        phone: 'phone',
        companyEmail: 'email',
        website: 'business_website',
        businessType: 'business_type',
        preferredLanguage: 'preferred_language',
        isMilitaryVeteran: 'is_military_veteran',
        isVeteranSpouse: 'is_veteran_spouse',
        yearFounded: 'business_established',
        numberOfEmployees: 'number_of_employees',
        isWomenOwned: 'women_owned',
        isMinorityOwned: 'minority_owned',
        selectedSupportTypesBackend: 'interested_products',
        applicationSummary: 'application_summary',
      };

      // Map form values to standardized property names
      const attributePropertyMap = {
        ownerRace: 'race',
        ownerEthnicity: 'ethnicity',
        ownerGender: 'gender',
        ownerDisability: 'disability_status',
        leadStatus: 'lead_status',
        statusNotes: 'status_notes',
        viabilityRating: 'viability_rating',
        seekingPartnership: 'seeking_partnership',
      };

      // Map loan details to standardized property names
      const loanPropertyMap = {
        stateRegisteredEntity: 'has_registered_entity',
        loanType: 'capital_product_type',
        willingToFillPrescreeningForm: 'will_fill_prescreening',
        requestingLoanAmount: 'loan_amount',
        loanTermMonths: 'loan_term_months',
        contributingPersonalCapital: 'contributing_personal_capital',
        personalCapitalPercentage: 'personal_capital_percentage',
        reportingPeriod: 'reporting_period',
        monthsInPeriod: 'period_months',
        totalRevenue: 'total_revenue',
        netProfit: 'net_profit',
        depreciation: 'depreciation_amortization',
        totalAssets: 'total_assets',
        cashEquivalents: 'cash_equivalents',
        tradeReceivables: 'receivables',
        inventoryValue: 'inventory_value',
        totalCurrentLiabilities: 'current_liabilities',
        shareholderEquity: 'shareholder_equity',
        alternativeNetWorth: 'alternative_net_worth',
        outstandingLoans: 'existing_loans_amount',
        approvedCreditLimit: 'credit_lines_limit',
        monthlyDebtService: 'monthly_debt_service',
        personalIncome: 'total_personal_income',
        personalNetWorth: 'personal_net_worth',
        personalLoans: 'personal_loans_amount',
        creditScoreRange: 'credit_score_range',
        haveCoSigner: 'has_co_signer',
        haveBusinessPlan: 'has_business_plan',
        haveFinancialProjections: 'has_financial_projections',
      };

      const NUMERIC_PROPERTIES = [
        'loan_amount',
        'loan_term_months',
        'personal_capital_percentage',
        'period_months',
        'total_revenue',
        'net_profit',
        'depreciation_amortization',
        'total_assets',
        'cash_equivalents',
        'receivables',
        'inventory_value',
        'current_liabilities',
        'shareholder_equity',
        'alternative_net_worth',
        'existing_loans_amount',
        'credit_lines_limit',
        'monthly_debt_service',
        'total_personal_income',
        'personal_net_worth',
        'personal_loans_amount',
        'number_of_employees',
      ];

      // Transform business details into properties format with mapped names, filtering out empty values
      const businessProperties = Object.entries(this.businessDetails)
        .filter(
          ([key, value]) =>
            key in businessPropertyMap &&
            value !== '' &&
            value !== null &&
            value !== undefined,
        )
        .map(([key, value]) => {
          const propName =
            businessPropertyMap[key as keyof typeof businessPropertyMap];
          let finalVal = value;
          if (NUMERIC_PROPERTIES.includes(propName)) {
            const num = Number(value);
            finalVal = isNaN(num) ? null : num;
          }
          return {
            property: propName,
            value: Array.isArray(finalVal)
              ? this.convertArrayToSnakeCase(finalVal)
              : finalVal,
          };
        });

      // Handle chamber/networking interest by adding to interested_products
      if (
        this.businessDetails.interestedInChamberOrBusinessNetworking === true
      ) {
        const interestedProductsProperty = businessProperties.find(
          (prop) => prop.property === 'interested_products',
        );

        if (interestedProductsProperty) {
          // Add chamber_or_networking to existing interested_products array
          if (Array.isArray(interestedProductsProperty.value)) {
            interestedProductsProperty.value.push('chamber_or_networking');
          }
        } else {
          // Create interested_products property with chamber_or_networking
          businessProperties.push({
            property: 'interested_products',
            value: ['chamber_or_networking'],
          });
        }
      }

      // Transform form values into properties format with mapped names, filtering out empty values
      const attributeProperties = Object.entries(formValues)
        .filter(
          ([key, value]) =>
            key in attributePropertyMap &&
            value !== '' &&
            value !== null &&
            value !== undefined,
        )
        .map(([key, value]) => ({
          property:
            attributePropertyMap[key as keyof typeof attributePropertyMap],
          value: value as string,
        }));

      // Transform loan details into properties format, filtering out empty values and nested objects
      let loanProperties: any[] = [];
      if (
        this.loanDetails &&
        this.isSelectedSupportType('Loans and Capital Products')
      ) {
        // Process simple loan fields
        loanProperties = Object.entries(this.loanDetails)
          .filter(
            ([key, value]) =>
              key in loanPropertyMap &&
              value !== '' &&
              value !== null &&
              value !== undefined &&
              typeof value !== 'object',
          )
          .map(([key, value]) => {
            const propName =
              loanPropertyMap[key as keyof typeof loanPropertyMap];
            let finalVal = value;
            if (NUMERIC_PROPERTIES.includes(propName)) {
              const num = Number(value);
              finalVal = isNaN(num) ? null : num;
            }
            return {
              property: propName,
              value: finalVal,
            };
          });

        // Process loanType array
        if (
          Array.isArray(this.loanDetails.loanType) &&
          this.loanDetails.loanType.length > 0
        ) {
          loanProperties.push({
            property: 'capital_product_type',
            value: this.convertArrayToSnakeCase(this.loanDetails.loanType),
          });
        }

        // Convert loanUse object → array
        const loanUseArray = this.mapCheckboxObjectToLabelArray(
          this.loanDetails.loanUse,
          this.projectDescriptionMap,
        );

        if (loanUseArray.length > 0) {
          loanProperties.push({
            property: 'project_description',
            value: loanUseArray,
          });
        }

        // Convert collateralTypes object → array
        const collateralTypesArray = this.mapCheckboxObjectToLabelArray(
          this.loanDetails.collateralTypes,
          this.collateralLabelMap,
        );

        if (collateralTypesArray.length > 0) {
          loanProperties.push({
            property: 'collateral_types',
            value: collateralTypesArray,
          });
        }

        // Process loanUse checkboxes
        if (
          Array.isArray(this.loanDetails.loanUse) &&
          this.loanDetails.loanUse.length > 0
        ) {
          loanProperties.push({
            property: 'project_description',
            value: this.convertArrayToSnakeCase(this.loanDetails.loanUse),
          });
        }

        // Process collateralTypes checkboxes
        if (
          Array.isArray(this.loanDetails.collateralTypes) &&
          this.loanDetails.collateralTypes.length > 0
        ) {
          loanProperties.push({
            property: 'collateral_types',
            value: this.convertArrayToSnakeCase(
              this.loanDetails.collateralTypes,
            ),
          });
        }

        // Process financial documentation checkboxes
        if (
          Array.isArray(this.loanDetails.financialDocumentation) &&
          this.loanDetails.financialDocumentation.length > 0
        ) {
          loanProperties.push({
            property: 'financial_documentation',
            value: this.convertArrayToSnakeCase(
              this.loanDetails.financialDocumentation,
            ),
          });
        }

        // Process ownership structure (for all loan types)
        if (
          Array.isArray(this.loanDetails.owner_ship_structure) &&
          this.loanDetails.owner_ship_structure.length > 0
        ) {
          // Transform ownership structure keys to snake_case
          const transformedOwnershipStructure =
            this.loanDetails.owner_ship_structure.map((owner: any) => ({
              owner_name: owner.ownerName,
              ownership_percentage: owner.ownershipPercentage,
              other_business_percentage: owner.otherBusinessesPercentage,
            }));

          loanProperties.push({
            property: 'ownership_structure',
            value: transformedOwnershipStructure,
          });
        }
      }

      // Transform technical assistance details
      const technicalAssistanceProperties =
        this.transformTechnicalAssistanceDetails();

      // Transform operational tools details
      const operationalToolsProperties =
        this.transformOperationalToolsDetails();

      // Transform real estate details
      const realEstateProperties = this.transformRealEstateDetails();

      // Transform solar energy details
      const solarEnergyProperties = this.transformSolarEnergyDetails();

      // Transform finding investors details
      const findingInvestorsProperties =
        this.transformFindingInvestorsDetails();

      const properties = [
        ...businessProperties,
        ...attributeProperties,
        ...loanProperties,
        ...technicalAssistanceProperties,
        ...operationalToolsProperties,
        ...realEstateProperties,
        ...solarEnergyProperties,
        ...findingInvestorsProperties,
      ];

      const payload = {
        source: 'CUSTOM',
        properties: properties,
      };

      this.addBusinessRecordService
        .saveBusinessRecord(payload, 'CUSTOM')
        .pipe(
          switchMap((res) => {
            if (this.hasGetSetLeadsPermission) {
              const company: Company = {
                leadId: undefined,
                unique_id: res.unique_id,
                company_name: this.businessDetails.businessName,
                short_address: this.businessDetails.addressLine1,
                assigned_to: null,
                source: 'CUSTOM',
                created_by_user: {
                  id: 0,
                  first_name:
                    this.authService.getLoggedInUserInfo()?.name || '',
                  last_name: '',
                  username:
                    this.authService.getLoggedInUserInfo()?.username || '',
                  connection: {
                    id: 0,
                    connection_name:
                      this.authService.getLoggedInUserInfo()?.connection || '',
                    display_name:
                      this.authService.getLoggedInUserInfo()
                        ?.connection_display_name || '',
                  },
                },
                created_on_utc: new Date(),
                assignees: null,
                is_active: true,
              };
              return this.leadService
                .assignLead([company])
                .pipe(catchError(() => of(res)));
            }
            return of(res);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (res) => {
            this.formSubmit.emit({
              success: true,
              response: res,
              message: 'Business record saved successfully',
            });
          },
          error: () => {
            this.formSubmit.emit({
              success: false,
              formData: this.leadAttributesForm.value,
              message: 'Failed to save business record!',
            });
          },
        });
    } else {
      this.markFormGroupTouched(this.leadAttributesForm);
    }
  }

  private collateralLabelMap: Record<string, string> = {
    realestatebuilding: 'real_estate_building',
    land: 'land',
    equipment: 'equipment',
    vehicle: 'vehicle',
    '3rdpartyguarantees': 'third_party_guarantees',
    residentialrealestate: 'residential_real_estate',
    inventory: 'inventory',
    accountsreceivable: 'accounts_receivable',
    cash: 'cash',
    investmentaccounts: 'investment_accounts',
    furnitureandfixtures: 'furniture_and_fixtures',
    newequipmentmachinery: 'new_equipment_machinery',
    leaseholdimprovements: 'leasehold_improvements',
    nocollateral: 'no_collateral',
  };

  private projectDescriptionMap: Record<string, string> = {
    businessacquisition: 'business_acquisition',
    equipmentmaintenance: 'equipment_maintenance',
    businessexpansion: 'business_expansion',
    equipmentpurchase: 'equipment_purchase',
    realestatepurchase: 'real_estate_purchase',
    realestateconstruction: 'real_estate_construction',
    realestateimprovements: 'real_estate_improvements',
    refinancebusinessdebt: 'refinance_business_debt',
    refinanceotherloan: 'refinance_other_loan',
    solarimprovements: 'solar_improvements',
    startupactivity: 'startup_activity',
    vehiclepurchase: 'vehicle_purchase',
    purchaseinventory: 'purchase_inventory',
  };

  private mapCheckboxObjectToLabelArray(
    obj: Record<string, boolean>,
    labelMap: Record<string, string>,
  ): string[] {
    if (!obj) {
      return [];
    }

    return Object.entries(obj)
      .filter(([, value]) => value === true)
      .map(([key]) => labelMap[key])
      .filter(Boolean); // safety
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  private isSelectedSupportType(supportType: string): boolean {
    return (
      this.businessDetails?.selectedSupportTypes?.includes(supportType) || false
    );
  }

  private toSnakeCase(str: string): string {
    return str
      .replace(/([A-Z])/g, '_$1')
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '')
      .toLowerCase()
      .replace(/^_/, '')
      .replace(/_+/g, '_');
  }

  private convertArrayToSnakeCase(arr: string[]): string[] {
    return arr.map((item) => this.toSnakeCase(item));
  }

  private transformTechnicalAssistanceDetails(): any[] {
    if (
      !this.technicalAssistanceDetails ||
      !this.isSelectedSupportType('Small Business Technical Assistance')
    )
      return [];

    const properties: any[] = [];

    // Add industry
    if (this.technicalAssistanceDetails.industry) {
      properties.push({
        property: 'business_industry',
        value: this.technicalAssistanceDetails.industry,
      });
    }

    // Add selected support areas
    if (this.technicalAssistanceDetails.selectedSupportAreas?.length > 0) {
      properties.push({
        property: 'business_help_aspects',
        value: this.convertArrayToSnakeCase(
          this.technicalAssistanceDetails.selectedSupportAreas,
        ),
      });
    }

    // Add succession planning details if available
    if (this.technicalAssistanceDetails.interestedInSellingBusiness) {
      properties.push({
        property: 'interested_in_selling_business',
        value: this.technicalAssistanceDetails.interestedInSellingBusiness,
      });
    }

    if (this.technicalAssistanceDetails.interestedInEmployeeStrategies) {
      properties.push({
        property: 'interested_in_employee_strategies',
        value: this.technicalAssistanceDetails.interestedInEmployeeStrategies,
      });
    }

    if (this.technicalAssistanceDetails.interestedInSuccessionPlanning) {
      properties.push({
        property: 'interested_in_succession_planning',
        value: this.technicalAssistanceDetails.interestedInSuccessionPlanning,
      });
    }

    // Add worked with support org
    if (this.technicalAssistanceDetails.workedWithSupportOrg) {
      properties.push({
        property: 'worked_with_support_org',
        value: this.technicalAssistanceDetails.workedWithSupportOrg,
      });
    }

    // Add willing to pay
    if (this.technicalAssistanceDetails.willingToPay) {
      properties.push({
        property: 'willing_to_pay_for_assistance',
        value: this.technicalAssistanceDetails.willingToPay,
      });
    }

    return properties;
  }

  private transformOperationalToolsDetails(): any[] {
    if (
      !this.operationalToolsDetails ||
      !this.isSelectedSupportType('Business Operational Tools')
    )
      return [];

    const properties: any[] = [];

    // Add selected tools from each category
    if (this.operationalToolsDetails.selectedTools) {
      const tools = this.operationalToolsDetails.selectedTools;

      if (tools.businessBanking?.length > 0) {
        properties.push({
          property: 'business_banking',
          value: this.convertArrayToSnakeCase(tools.businessBanking),
        });
      }

      if (tools.marketingHelp?.length > 0) {
        properties.push({
          property: 'marketing_help',
          value: this.convertArrayToSnakeCase(tools.marketingHelp),
        });
      }

      if (tools.techSupport?.length > 0) {
        properties.push({
          property: 'tech_support',
          value: this.convertArrayToSnakeCase(tools.techSupport),
        });
      }

      if (tools.businessInsurance?.length > 0) {
        properties.push({
          property: 'business_insurance',
          value: this.convertArrayToSnakeCase(tools.businessInsurance),
        });
      }

      if (tools.newTechnology?.length > 0) {
        properties.push({
          property: 'new_technology',
          value: this.convertArrayToSnakeCase(tools.newTechnology),
        });
      }
    }

    return properties;
  }

  private transformRealEstateDetails(): any[] {
    if (
      !this.realEstateDetails ||
      !this.isSelectedSupportType('Commercial Real Estate')
    )
      return [];

    const properties: any[] = [];

    // Add looking to rent or buy
    if (
      this.realEstateDetails.lookingToRentOrBuy !== '' &&
      this.realEstateDetails.lookingToRentOrBuy !== null &&
      this.realEstateDetails.lookingToRentOrBuy !== undefined
    ) {
      properties.push({
        property: 'looking_to_rent_or_buy',
        value: this.realEstateDetails.lookingToRentOrBuy,
      });
    }

    // Add looking to upgrade
    if (
      this.realEstateDetails.lookingToUpgrade !== '' &&
      this.realEstateDetails.lookingToUpgrade !== null &&
      this.realEstateDetails.lookingToUpgrade !== undefined
    ) {
      properties.push({
        property: 'looking_to_upgrade_facility',
        value: this.realEstateDetails.lookingToUpgrade,
      });
    }

    // Add own facility
    if (
      this.realEstateDetails.ownFacility !== '' &&
      this.realEstateDetails.ownFacility !== null &&
      this.realEstateDetails.ownFacility !== undefined
    ) {
      properties.push({
        property: 'own_facility',
        value: this.realEstateDetails.ownFacility,
      });
    }

    // Add building owner willing to support
    if (
      this.realEstateDetails.buildingOwnerWillingToSupport !== '' &&
      this.realEstateDetails.buildingOwnerWillingToSupport !== null &&
      this.realEstateDetails.buildingOwnerWillingToSupport !== undefined
    ) {
      properties.push({
        property: 'building_owner_willing_to_support',
        value: this.realEstateDetails.buildingOwnerWillingToSupport,
      });
    }

    // Add interested in energy upgrades
    if (
      this.realEstateDetails.interestedInEnergyUpgrades !== '' &&
      this.realEstateDetails.interestedInEnergyUpgrades !== null &&
      this.realEstateDetails.interestedInEnergyUpgrades !== undefined
    ) {
      properties.push({
        property: 'interested_in_energy_upgrades',
        value: this.realEstateDetails.interestedInEnergyUpgrades,
      });
    }

    return properties;
  }

  private transformSolarEnergyDetails(): any[] {
    if (
      !this.solarEnergyDetails ||
      !this.isSelectedSupportType('Solar and Energy Efficiency Products')
    )
      return [];

    const properties: any[] = [];

    // Add interested in solar
    if (
      this.solarEnergyDetails.interestedInSolar !== '' &&
      this.solarEnergyDetails.interestedInSolar !== null &&
      this.solarEnergyDetails.interestedInSolar !== undefined
    ) {
      properties.push({
        property: 'interested_in_solar_provider_or_loan',
        value: this.solarEnergyDetails.interestedInSolar,
      });
    }

    // Add interested in energy efficiency
    if (
      this.solarEnergyDetails.interestedInEnergyEfficiency !== '' &&
      this.solarEnergyDetails.interestedInEnergyEfficiency !== null &&
      this.solarEnergyDetails.interestedInEnergyEfficiency !== undefined
    ) {
      properties.push({
        property: 'interested_in_e_e_upgrades',
        value: this.solarEnergyDetails.interestedInEnergyEfficiency,
      });
    }

    // Add building age
    if (this.solarEnergyDetails.buildingAge) {
      properties.push({
        property: 'building_built_year',
        value: this.solarEnergyDetails.buildingAge,
      });
    }

    // Add building improvements
    if (this.solarEnergyDetails.buildingImprovements) {
      properties.push({
        property: 'building_improvements',
        value: this.solarEnergyDetails.buildingImprovements,
      });
    }

    // Add power providers
    if (this.solarEnergyDetails.powerProviders) {
      properties.push({
        property: 'power_utility_providers',
        value: this.solarEnergyDetails.powerProviders,
      });
    }

    // Add interested in batteries
    if (this.solarEnergyDetails.interestedInBatteries) {
      properties.push({
        property: 'interested_in_batteries',
        value: this.solarEnergyDetails.interestedInBatteries,
      });
    }

    // Add can provide power statement
    if (
      this.solarEnergyDetails.canProvidePowerStatement !== '' &&
      this.solarEnergyDetails.canProvidePowerStatement !== null &&
      this.solarEnergyDetails.canProvidePowerStatement !== undefined
    ) {
      properties.push({
        property: 'can_provide_power_use_statement',
        value: this.solarEnergyDetails.canProvidePowerStatement,
      });
    }

    // Add has electric vehicles
    if (
      this.solarEnergyDetails.hasElectricVehicles !== '' &&
      this.solarEnergyDetails.hasElectricVehicles !== null &&
      this.solarEnergyDetails.hasElectricVehicles !== undefined
    ) {
      properties.push({
        property: 'has_electric_vehicles',
        value: this.solarEnergyDetails.hasElectricVehicles,
      });
    }

    // Add interested in electric vehicle
    if (
      this.solarEnergyDetails.interestedInElectricVehicle !== '' &&
      this.solarEnergyDetails.interestedInElectricVehicle !== null &&
      this.solarEnergyDetails.interestedInElectricVehicle !== undefined
    ) {
      properties.push({
        property: 'looking_to_add_electric_vehicle',
        value: this.solarEnergyDetails.interestedInElectricVehicle,
      });
    }

    // Add credit score range (if provided in solar form)
    if (this.solarEnergyDetails.creditScoreRange) {
      properties.push({
        property: 'credit_score_range',
        value: this.solarEnergyDetails.creditScoreRange,
      });
    }

    return properties;
  }

  private transformFindingInvestorsDetails(): any[] {
    if (
      !this.findingInvestorsDetails ||
      !this.isSelectedSupportType('Finding Investors')
    )
      return [];

    const properties: any[] = [];

    // Add know investor type
    if (this.findingInvestorsDetails.knowInvestorType) {
      properties.push({
        property: 'knows_investor_type',
        value: this.findingInvestorsDetails.knowInvestorType,
      });
    }

    // Add selected investment types
    if (this.findingInvestorsDetails.selectedInvestmentTypes?.length > 0) {
      properties.push({
        property: 'investment_types',
        value: this.convertArrayToSnakeCase(
          this.findingInvestorsDetails.selectedInvestmentTypes,
        ),
      });
    }

    // Add willing to relocate
    if (this.findingInvestorsDetails.willingToRelocate) {
      properties.push({
        property: 'willing_to_relocate',
        value: this.findingInvestorsDetails.willingToRelocate,
      });
    }

    return properties;
  }
}
